import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Car, Clock, Shield, Star, MapPin, Phone, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

const CarServiceWimberley = () => {
  // Updated February 2026 - Formula pricing (no flat rate)
  // ~55 miles, ~60 min → $50 + 55*3.25 + 60*1.25 = $50 + $178.75 + $75 = $303.75 + 20% = ~$364

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Rick's Black Car Service - Wimberley",
    "description": "Premium black car service from Wimberley to San Antonio Airport. Luxury Audi Q7 transportation with transparent pricing.",
    "url": "https://www.ricksblackcarservicesantonio.com/wimberley-car-service",
    "telephone": "+1-210-296-7409",
    "email": "ricksblackcarservice@outlook.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Wimberley",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 29.9974,
      "longitude": -98.0986
    },
    "areaServed": ["Wimberley", "San Marcos", "Dripping Springs", "San Antonio", "Hill Country"],
    "priceRange": "$$",
    "openingHours": "Mo-Su 00:00-23:59"
  };

  return (
    <>
      <Helmet>
        <title>Luxury Black Car Service Wimberley to San Antonio Airport | Rick's Black Car</title>
        <meta 
          name="description" 
          content="Premium Wimberley private driver & airport transfer service. Direct transport to SAT in our 2025 Audi Q7. Transparent pricing based on distance & time." 
        />
        <meta name="keywords" content="Wimberley private driver, airport transfer Wimberley, Wimberley to San Antonio airport, luxury car service Wimberley TX, Hill Country transportation" />
        <link rel="canonical" href="https://www.ricksblackcarservicesantonio.com/wimberley-car-service" />
        <meta property="og:title" content="Luxury Black Car Service Wimberley to San Antonio Airport" />
        <meta property="og:description" content="Direct transport from Wimberley to SAT Airport in our 2025 Audi Q7. Professional, reliable, transparent pricing." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1920&q=80')`,
              filter: 'brightness(0.7)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
          
          <div className="relative z-10 container mx-auto px-4 py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Serving Wimberley & Dripping Springs</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Luxury Black Car Service<br />
              <span className="text-primary">Wimberley to San Antonio Airport</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Direct transport from Wimberley & Dripping Springs to SAT Airport.<br />
              <span className="text-primary font-semibold">Reliable. Professional. On Time.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Your Ride</a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10">
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  (210) 296-7409
                </a>
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-3xl font-bold text-primary">~55</div>
                <div className="text-sm text-gray-300">Miles to SAT</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-3xl font-bold text-primary">~60</div>
                <div className="text-sm text-gray-300">Minutes Drive</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-4">
                <div className="text-2xl font-bold text-primary">Get Quote</div>
                <div className="text-sm text-gray-300">Instant Pricing</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Choose Rick's Black Car for Your Wimberley Airport Transfer?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Experience the difference of premium transportation from the scenic Hill Country to San Antonio.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Car className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">2025 Audi Q7 Black Optic</h3>
                  <p className="text-muted-foreground">
                    Quiet cabin, premium leather, WiFi, and USB charging. Perfect for a relaxing scenic drive.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Always On Time</h3>
                  <p className="text-muted-foreground">
                    Flight monitoring included. We track delays and adjust pickup times automatically.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Professional Chauffeur</h3>
                  <p className="text-muted-foreground">
                    Rick personally handles every ride. No random drivers. Consistent, reliable service every time.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">5-Star Rated</h3>
                  <p className="text-muted-foreground">
                    Trusted by Wimberley residents for airport transfers, business travel, and special events.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Transparent Pricing Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Transparent Pricing — No Surprises
                </h2>
                <p className="text-lg text-muted-foreground">
                  Pricing based on distance & time with 20% gratuity included. Get an instant quote for your exact route.
                </p>
              </div>

              <Card className="bg-card border-2 border-primary/30 overflow-hidden">
                <div className="bg-primary/10 px-6 py-4 border-b border-primary/20">
                  <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Wimberley ↔ San Antonio Airport (SAT)
                  </h3>
                </div>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-foreground mb-4">Route Details</h4>
                      <ul className="space-y-3 text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Distance: ~55 miles via I-35 South
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Duration: ~60 minutes
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Vehicle: 2025 Audi Q7 Black Optic
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          Flight monitoring included
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-6">
                      <h4 className="font-semibold text-foreground mb-4">Pricing</h4>
                      <div className="space-y-2 text-sm">
                        <p className="text-muted-foreground">Based on distance & time formula with 20% gratuity included.</p>
                        <div className="border-t border-border pt-2 mt-2">
                          <Button asChild className="w-full">
                            <Link to="/quick-quote">Get Instant Quote</Link>
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        Late-Night/Early Morning Surcharge ($35) applies for pickups 9 PM - 5 AM.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                    <Button asChild size="lg" className="text-lg">
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Book This Route
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                      <Link to="/quick-quote">Get Custom Quote</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* About Wimberley Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                About Wimberley, Texas
              </h2>
              <div className="prose prose-lg mx-auto text-muted-foreground">
                <p className="text-center mb-8">
                  Wimberley is a charming Hill Country town known for its stunning natural beauty, artisan shops, 
                  and the famous Blue Hole swimming spot. Located in Hays County, this artistic community attracts 
                  visitors and residents who appreciate a slower pace of life surrounded by cypress trees and 
                  crystal-clear springs.
                </p>
                <p className="text-center">
                  Whether you're traveling from Wimberley for business, connecting to a flight at SAT Airport, 
                  or need reliable transportation for a special event in San Antonio, Rick's Black Car Service 
                  provides the premium experience you deserve.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                What Hill Country Residents Say
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic">
                    "Living in Wimberley means a longer drive to the airport, but Rick makes it feel like a luxury experience. 
                    The Audi Q7 is incredibly comfortable and I can catch up on emails during the drive. Worth every penny!"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold">DM</span>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">David Martinez</div>
                      <div className="text-sm text-muted-foreground">Wimberley, TX</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic">
                    "We hosted a destination wedding in Wimberley and Rick provided transportation for our out-of-town guests 
                    from the San Antonio airport. Professional, punctual, and our guests raved about the service!"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold">KW</span>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">Karen Williams</div>
                      <div className="text-sm text-muted-foreground">Wimberley, TX</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary/10 border-y border-primary/20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Experience Premium Transportation?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Book your Wimberley to San Antonio Airport transfer today. Professional service, transparent pricing, luxury comfort.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <Link to="/quick-quote">Get Instant Quote</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  Call (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Also Serving Nearby Areas
              </h2>
              <p className="text-muted-foreground">
                Premium transportation throughout the Hill Country
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
              {["Dripping Springs", "San Marcos", "Kyle", "Buda", "Austin", "New Braunfels", "San Antonio"].map((area) => (
                <span 
                  key={area}
                  className="bg-muted px-4 py-2 rounded-full text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default CarServiceWimberley;
